import { ControlTypes, uuidv4 } from '../../../utils/helper';
import * as Yup from 'yup';
import cloneDeep from 'lodash/cloneDeep';
import isArray from 'lodash/isArray';
import maxBy from 'lodash/maxBy';
import { STATUSES } from '../../../utils/Statuses';
import { getQuestionTypeConfig } from './questionTypeRegistry';
import MultipleChoiceIcon from '../../../app/components/svgIcons/MultipleChoiceIcon';
import EyeIcon from '../../../app/components/svgIcons/EyeIcon';
import React from 'react';

export const SENT_FORM_LOCK_MESSAGE =
  'This form has been sent to a candidate and cannot be edited or deleted.';

export const FORM_TAB_IDS = {
  BUILD: 1,
  DESIGN: 2,
  RESPONSES: 3,
  SETTINGS: 4,
  PREVIEW: 5,
};

export const formTabs = [
  {
    id: FORM_TAB_IDS.BUILD,
    title: 'Build',
    active: true,
  },
  {
    id: FORM_TAB_IDS.DESIGN,
    title: 'Design',
    active: false,
  },
  {
    id: FORM_TAB_IDS.PREVIEW,
    title: 'Preview',
    active: false,
    icon: <EyeIcon size={16} notActionIcon color="currentColor" />,
  },
  {
    id: FORM_TAB_IDS.SETTINGS,
    title: 'Settings',
    active: false,
  },
  {
    id: FORM_TAB_IDS.RESPONSES,
    title: 'Responses',
    active: false,
  },
];

export const refFormTabs = [
  {
    id: FORM_TAB_IDS.BUILD,
    title: 'Build',
    active: true,
  },
  {
    id: FORM_TAB_IDS.DESIGN,
    title: 'Design',
    active: false,
  },
  {
    id: FORM_TAB_IDS.PREVIEW,
    title: 'Preview',
    active: false,
    icon: <EyeIcon size={16} notActionIcon color="currentColor" />,
  },
  {
    id: FORM_TAB_IDS.RESPONSES,
    title: 'Responses',
    active: false,
  },
];
export const createFormSchema = {
  title: Yup.string().required('Required'),
  type: Yup.number().required('Required'),
};

export const formTypes = {
  CANDIDATE: 1,
  APPLICATION: 1,
  INTERNAL: 4,
  INTERVIEW: 4,
  ON_BOARDING: 6,
  DEI: 7,
  REFERENCE: 8,
  SCREENING: 10,
  FEEDBACK: 11,
};

export const getFormBuilderTabs = (type) => {
  if (type == null || type === '') return [];
  const typeNumber = Number(type);
  if (Number.isNaN(typeNumber)) return [];
  if (typeNumber === formTypes.REFERENCE) return refFormTabs;
  if (typeNumber === formTypes.INTERNAL) {
    return formTabs.filter((tab) => tab.id !== FORM_TAB_IDS.DESIGN);
  }
  return formTabs;
};

export const formTypeEnums = {
  APPLICATION_FORM: 1,
  QUESTIONNAIRE_FORM: 4,
  ON_BOARDING_FORM: 6,
  DEI_FORM: 7,
  BG_REFERENCE_FORM: 8,
  SCREENING_FORM: 10,
  FEEDBACK_NPS_FORM: 11,
};

export const formCategories = [
  { label: 'Application', enum: 'APPLICATION_FORM', value: formTypeEnums.APPLICATION_FORM },
  { label: 'Interview', enum: 'QUESTIONNAIRE_FORM', value: formTypeEnums.QUESTIONNAIRE_FORM },
  { label: 'Onboarding / Offer', enum: 'ON_BOARDING_FORM', value: formTypeEnums.ON_BOARDING_FORM },
  // { label: 'DE&I', enum: 'DEI_FORM', value: formTypeEnums.DEI_FORM },
  { label: 'Reference', enum: 'BG_REFERENCE_FORM', value: formTypeEnums.BG_REFERENCE_FORM },
  { label: 'Screening', enum: 'SCREENING_FORM', value: formTypeEnums.SCREENING_FORM },
  { label: 'Feedback / NPS', enum: 'FEEDBACK_NPS_FORM', value: formTypeEnums.FEEDBACK_NPS_FORM },
];

export const getFormTypeLabel = (type) =>
  formCategories.find((category) => category.value === Number(type))?.label || 'Interview';

export const formStateFromTemplate = (template, title) => {
  const category = formCategories.find((item) => item.value === Number(template?.type));
  const pages = (template?.pages || []).map((page) => ({
    id: page.clientKey,
    clientKey: page.clientKey,
    title: page.title,
    pageOrder: page.pageOrder,
  }));
  const formQuestions = (template?.formQuestions || []).map((question, index) => ({
    clientId: `tmp-${question.pageId || 'page'}-${question.sectionOrder || index + 1}`,
    sectionLabel: question.sectionLabel,
    sectionType: question.sectionType,
    typeKey: question.sectionOptions?.typeKey,
    sectionOptions: question.sectionOptions,
    sectionOrder: question.sectionOrder,
    pageId: question.pageId,
    isRequired: Boolean(question.isRequired),
    active: true,
  }));

  return {
    title: (title || '').trim() || template?.title || '',
    type: template?.type,
    typeEnum: category?.enum,
    pages,
    formQuestions,
    sections: template?.sections || [],
    welcomeMessage: template?.welcomeMessage,
    refOpts: template?.refOpts,
  };
};

/** Drop logo and banner so a new form does not inherit another form's images. */
export const withoutFormMedia = (form = {}) => ({
  ...form,
  logoMedia: null,
  bannerMedia: null,
  compFormSetting: {
    ...(form.compFormSetting || {}),
    logo: null,
    banner: null,
    logoUrl: '',
    bannerUrl: '',
  },
});

export const formStatuses = {
  SUBMITTED: 45,
  SENT: 16,
  VIEWED: 13,
  PARTIALLY_COMPLETED: 1,
};

export const FORM_LISTING_TAB_IDS = {
  ALL: 1,
  PUBLISHED: 2,
  DRAFTS: 3,
  ARCHIVED: 4,
};

export const formListingTabs = [
  { id: FORM_LISTING_TAB_IDS.ALL, title: 'All', active: true },
  { id: FORM_LISTING_TAB_IDS.PUBLISHED, title: 'Published', active: false },
  { id: FORM_LISTING_TAB_IDS.DRAFTS, title: 'Drafts', active: false },
  { id: FORM_LISTING_TAB_IDS.ARCHIVED, title: 'Archived', active: false },
];

export const getFormListingStatusId = (tabId) =>
  ({
    [FORM_LISTING_TAB_IDS.PUBLISHED]: STATUSES.LIVE,
    [FORM_LISTING_TAB_IDS.DRAFTS]: STATUSES.DRAFT,
    [FORM_LISTING_TAB_IDS.ARCHIVED]: STATUSES.ARCHIVED,
  }[tabId]);

export const FORM_STATUS_LABELS = {
  [STATUSES.LIVE]: 'Published',
  [STATUSES.DRAFT]: 'Draft',
  [STATUSES.ARCHIVED]: 'Archived',
};

export const getFormStatusLabel = (statusId) =>
  FORM_STATUS_LABELS[Number(statusId)] || 'Draft';

const LISTING_QUERY_KEYS = new Set(['perPage', 'page', 'query', 'sortedColumn']);

/** Filter fields only. Listing page, search, and sort must not be mixed in. */
export const appliedFormFilters = (filters) => {
  if (!filters || typeof filters !== 'object') return {};
  return Object.fromEntries(
    Object.entries(filters).filter(
      ([key, value]) =>
        !LISTING_QUERY_KEYS.has(key) &&
        value != null &&
        value !== '' &&
        typeof value !== 'object',
    ),
  );
};

export const hasAppliedFormFilters = (filters) =>
  Object.keys(appliedFormFilters(filters)).length > 0;

/** Question options are stored as `{ label }` and selected by their label. */
export const toLabelOptions = (opts, isReadOnly = false) =>
  (opts || []).map(({ label }) => ({ label, value: isReadOnly ? '' : label }));

export const getSectionOrder = (questions = []) =>
  questions?.length ? maxBy(questions, 'sectionOrder')?.sectionOrder + 1 : 1;

/** Stable identity across drag/reorder — never use sectionOrder for selection. */
export const getQuestionKey = (question) => {
  if (!question) return null;
  if (question.id != null && question.id !== '') return `id:${question.id}`;
  if (question.clientId) return `client:${question.clientId}`;
  return null;
};

export const findQuestionIndex = (questions, question) => {
  const list = questions || [];
  if (question?.id != null && question.id !== '') {
    const byId = list.findIndex((item) => item?.id === question.id);
    if (byId > -1) return byId;
  }
  if (question?.clientId) {
    const byClient = list.findIndex((item) => item?.clientId === question.clientId);
    if (byClient > -1) return byClient;
  }
  return list.findIndex((item) => item?.sectionOrder === question?.sectionOrder);
};

/**
 * Saved questions are soft deleted so the API can reconcile them, unsaved ones
 * are dropped outright. Returns null when the question is no longer in the form.
 */
export const removeQuestionFromForm = (form, question) => {
  const updatedForm = cloneDeep(form);
  const questions = updatedForm?.formQuestions || [];

  if (question?.id) {
    const index = questions.findIndex((item) => item?.id === question.id);
    if (index < 0) return null;
    questions[index].statusId = STATUSES.DELETE;
    updatedForm.deletedQuestions = [
      ...(updatedForm?.deletedQuestions || []),
      ...questions.filter((item) => item.statusId === STATUSES.DELETE),
    ];
  } else {
    updatedForm.formQuestions = questions.filter(
      (item) =>
        item.statusId === STATUSES.DELETE ||
        (question?.clientId
          ? item.clientId !== question.clientId
          : item.sectionOrder !== question?.sectionOrder),
    );
  }

  return updatedForm;
};

export {
  getQuestionTypeConfig,
  getBuilderQuestionTypes,
  getQuestionTypesByCategory,
  createQuestionFromType,
  createTempClientId,
} from './questionTypeRegistry';

export const groups = [
  {plabel: "White"},
  {plabel: "Mixed/Multiple ethnic groups"},
  {plabel: "Asian/Asian British"},
  {plabel: "Black/Black British/Caribbean/African"},
  {plabel: "Other ethnic group"},
  {plabel: "Not Disclosed"},
  ];

export const handleUpdateControl = (form, updateData, isReset, controlIndex, sectionIndex ) => {
  if (sectionIndex === undefined && controlIndex === undefined) {
    console.error('Invalid activeIndex provided.');
    return;
  }
  const updatedForm = cloneDeep(form);

  if (sectionIndex >= 0) {
    const section = updatedForm?.formQuestions[sectionIndex];

    if (!section || !section.controls || !section.controls[controlIndex]) {
      console.error('Invalid controlIndex or sectionIndex provided.');
      return;
    }

    const updatedControls = [...section.controls];
    updatedControls[controlIndex] = {
      ...(!isReset && updatedControls[controlIndex]),
      ...updateData,
    };
    updatedForm.formQuestions[sectionIndex] = {
      ...section,
      controls: updatedControls,
    };
  } else {
    const control = updatedForm?.formQuestions[controlIndex];

    if (!control) {
      console.error('Invalid controlIndex provided.');
      return;
    }

    updatedForm.formQuestions[controlIndex] = {
      ...(!isReset && control),
      ...updateData,
    };
  }

  return updatedForm;
};

export const getNestedControls = (questions) => {
  if (!isArray(questions)) {
    return [];
  }
  const formQuestions = questions?.reduce((acc, curr) => {
    if (curr?.controls) {
      acc = [...acc, ...curr.controls];
    } else {
      acc.push(curr);
    }
    return acc;
  }, []);

  return formQuestions?.filter(
    (question) =>
      question.sectionType !== ControlTypes.SECTION &&
      question.statusId !== STATUSES.DELETE,
  );
};

export const shortQuestions = [
  {
    sectionOrder: 1,
    shortTitle: 'Ethnicity',
  },
  {
    sectionOrder: 2,
    shortTitle: 'Gender',
  },
  {
    sectionOrder: 3,
    shortTitle: 'Disability',
  },
  {
    sectionOrder: 4,
    shortTitle: 'Age',
  },
  {
    sectionOrder: 5,
    shortTitle: 'Nationality',
  },
  {
    sectionOrder: 6,
    shortTitle: 'Sexual Orientation',
  },
  {
    sectionOrder: 7,
    shortTitle: 'Religion',
  },
];



export const getIconBySectionType = (sectionType) =>
  getQuestionTypeConfig(sectionType)?.icon || null;

export const getControlLabelBySectionType = (sectionType) =>
  getQuestionTypeConfig(sectionType)?.sectionLabel || null;

export const getQuestionMeta = (question) => {
  const config = getQuestionTypeConfig(question);
  const sectionLabel =
    config?.sectionLabel ||
    getControlLabelBySectionType(question?.sectionType);
  return {
    config,
    Icon: config?.icon || getIconBySectionType(question?.sectionType),
    sectionLabel: sectionLabel || '',
    badgeLabel: config?.badgeLabel || sectionLabel || question?.sectionType,
    description: config?.description || 'Question settings',
  };
};

export const createDeiSection = (deiForm, extras = {}) => ({
  sectionLabel: 'DE&I',
  sectionType: ControlTypes.SECTION,
  icon: MultipleChoiceIcon,
  description: 'Questions on Diversity, Equality and Inclusion',
  controls: [...(deiForm?.formQuestions || [])],
  isSensitive: true,
  active: true,
  ...extras,
});

export const updateAt = (list, index, patch) =>
  (list || []).map((item, itemIndex) => {
    if (itemIndex !== index) return item;
    return typeof patch === 'function' ? patch(item) : { ...item, ...patch };
  });

export const DEFAULT_FORM_PAGE_ID = 'page-1';

export const createFormPage = (overrides = {}) => ({
  id: uuidv4(),
  title: 'Untitled page',
  description: '',
  ...overrides,
});

export const getDefaultFormPages = () => [
  createFormPage({ id: DEFAULT_FORM_PAGE_ID, title: 'Page 1' }),
];

export const ensureFormPages = (form) =>
  form?.pages?.length ? form.pages : getDefaultFormPages();

/** Candidate steps follow pages. One page is one step, even with many questions. */
export const resolveFormViewType = () => 'paged';

export const MAX_QUESTIONS_PER_PAGE = 15;

export const pageQuestionLimitMessage = () =>
  `A page can have at most ${MAX_QUESTIONS_PER_PAGE} questions`;

export const getQuestionPageId = (question, pages = []) =>
  question?.pageId || pages[0]?.id || DEFAULT_FORM_PAGE_ID;

export const getPageQuestions = (questions, pageId, pages) =>
  (questions || []).filter(
    (question) =>
      !question?.isSensitive &&
      question?.statusId !== STATUSES.DELETE &&
      getQuestionPageId(question, pages) === pageId,
  );

export const getPageQuestionLimitError = (form) => {
  const pages = form?.pages?.length ? form.pages : [];
  if (!pages.length) return null;
  const questions = form?.formQuestions || [];
  const overLimit = pages.find(
    (page) =>
      getPageQuestions(questions, page.id, pages).length > MAX_QUESTIONS_PER_PAGE,
  );
  return overLimit ? pageQuestionLimitMessage() : null;
};

export const mergePageQuestions = (questions, pageId, pageQuestions, pages) => {
  const currentQuestions = questions || [];
  const remaining = currentQuestions.filter(
    (question) =>
      question?.isSensitive || getQuestionPageId(question, pages) !== pageId,
  );
  const firstIndex = currentQuestions.findIndex(
    (question) =>
      !question?.isSensitive && getQuestionPageId(question, pages) === pageId,
  );
  const insertAt = firstIndex === -1 ? remaining.length : firstIndex;
  const next = [...remaining];
  next.splice(insertAt, 0, ...pageQuestions);
  return next;
};